import moment from 'moment';
import Vue from 'vue';

import actionFactory from '../../lib/actionFactory';

export default {
  ...actionFactory({
    singular: 'dashboard',
    plural: 'dashboards'
  }),
  ...actionFactory(
    {
      singular: 'dashboard',
      plural: 'dashboards'
    },
    {
      singular: 'widget',
      plural: 'widgets'
    }
  ),
  getAnalytics: async ({ commit }, { id, params }) => {
    try {
      const { body } = await Vue.http.get(`${process.env.VUE_APP_ANALYTICS_URL}/v2`, {
        params,
        timeout: 600000,
        before: request => {
          commit('GET_ANALYTICS_REQUEST', { id, params, request });
        }
      });
      commit('GET_ANALYTICS_SUCCESS', { id, body });
      return body;
    } catch (e) {
      // We are aborting requests, so only error if the body contains an error
      if (e.body && e.body.error) {
        commit('GET_ANALYTICS_ERROR', { id, e });
      }
      return e;
    }
  },
  getWidgetData: async ({ commit }, { dashboardId, widgetId, params }) => {
    try {
      const { body } = await Vue.http.get(`${process.env.VUE_APP_API_URL}/dashboards/${dashboardId}/widgets/${widgetId}/data`, { params });
      commit('REFRESH_WIDGET_SUCCESS', { id: widgetId, body });
      return body;
    } catch (e) {
      commit('REFRESH_WIDGET_ERROR', { id: widgetId, error: e.body?.error });
      return e;
    }
  },
  refreshWidget: async ({ dispatch, commit, state }, { id, params, auth }) => {
    if (!auth || !auth.permissions || !auth.isCompanyUser) {
      commit('REFRESH_WIDGET_ERROR', { id, error: 'No permissions provided' });
      return;
    }

    commit('REFRESH_WIDGET_REQUEST', { id });

    const widget = state.selectedDashboard?.widgets?.find(w => w._id === id);
    if (!widget) {
      commit('REFRESH_WIDGET_ERROR', { id, error: 'Widget not found' });
      return;
    }

    const settings = { ...widget.settings, ...widget.settings?.customData };

    // handle auth permissions for analytics
    if (!auth.isAdmin && !auth.isCompanyUser()) {
      const permissionType = settings.type === 'entity' ? 'entities' : `${settings.type}s`;
      const permissionIds = auth.permissions[permissionType].read;
      const hasAccess = permissionIds.includes(settings.selectedId);
      if (!hasAccess) {
        commit('REFRESH_WIDGET_ERROR', { id, error: 'You do not have access to this data' });
        return;
      }
    }

    const startDate = moment(state.selectedDashboard.startDate);
    const endDate = moment(state.selectedDashboard.endDate);

    if (!settings.consumptionType) {
      await dispatch('getWidgetData', {
        dashboardId: state.selectedDashboard._id,
        widgetId: id,
        params: { ...params, startDate: startDate.format('YYYY-MM-DD'), endDate: endDate.format('YYYY-MM-DD') }
      });
      return;
    }

    const consumptionParams = {
      id: 'consumption',
      dataType: settings.type,
      source: settings.source
    };

    if (settings.granularity) {
      consumptionParams.granularity = settings.granularity;
    }

    if (settings.type === 'assetGroup') {
      consumptionParams.assetGroupId = settings.selectedId;
    } else if (settings.type === 'asset') {
      consumptionParams.assetId = settings.selectedId;
    } else if (settings.type === 'account') {
      consumptionParams.accountId = settings.selectedId;
    } else if (settings.type === 'entity') {
      consumptionParams.entityId = settings.selectedId;
    }

    if (settings.metric === 'intensity') {
      consumptionParams.floorArea = true;
    }

    let prevStartDate = null;
    let prevEndDate = null;

    if (settings.comparePeriod) {
      // We want to get the previous period.
      // However, if the current period is a full month, we want to get the previous month.
      // If the current period is a full year, we want to get the previous year.
      // If it's a partial month, we just want the previous period (e.g. 5th - 10th, prev period would be 1st - 5th)
      const isFullMonth = startDate.date() === 1 && endDate.date() === endDate.daysInMonth();
      const isFullYear = startDate.month() === 0 && startDate.date() === 1 && endDate.month() === 11 && endDate.date() === endDate.daysInMonth();

      if (isFullMonth) {
        prevStartDate = moment(startDate).subtract(1, 'month').startOf('month');
        prevEndDate = moment(endDate).subtract(1, 'month').endOf('month');
      } else if (isFullYear) {
        prevStartDate = moment(startDate).subtract(1, 'year').startOf('year');
        prevEndDate = moment(endDate).subtract(1, 'year').endOf('year');
      } else {
        const days = endDate.diff(startDate, 'days') + 1;
        prevStartDate = moment(startDate).subtract(days, 'days');
        prevEndDate = moment(endDate).subtract(days, 'days');
      }
    }

    const promises = [];

    if (settings.consumptionType === 'emissions') {
      consumptionParams.emissions = true;

      promises.push(
        dispatch('getAnalytics', {
          id: id,
          params: {
            ...consumptionParams,
            id: 'emissions',
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD')
          }
        })
      );

      if (settings.comparePeriod) {
        promises.push(
          dispatch('getAnalytics', {
            id: `${id}-prev`,
            params: {
              ...consumptionParams,
              id: 'emissions',
              startDate: prevStartDate.format('YYYY-MM-DD'),
              endDate: prevEndDate.format('YYYY-MM-DD')
            }
          })
        );
      }
    } else {
      consumptionParams.accountType = settings.utilityType;

      promises.push(
        dispatch('getAnalytics', {
          id: id,
          params: {
            ...consumptionParams,
            startDate: moment(startDate).format('YYYY-MM-DD'),
            endDate: moment(endDate).format('YYYY-MM-DD')
          }
        })
      );

      if (settings.comparePeriod) {
        promises.push(
          dispatch('getAnalytics', {
            id: `${id}-prev`,
            params: {
              ...consumptionParams,
              startDate: prevStartDate.format('YYYY-MM-DD'),
              endDate: prevEndDate.format('YYYY-MM-DD')
            }
          })
        );
      }
    }

    const results = await Promise.all(promises);

    commit('REFRESH_WIDGET_SUCCESS', { id, body: results });
  },
  refreshWidgets: async ({ dispatch, state }, { auth }) => {
    const promises = state.selectedDashboard.widgets.map(widget => {
      return dispatch('refreshWidget', { id: widget._id, auth });
    });

    await Promise.all(promises);
  }
};
